import { LayoutDirection } from '@/types';
import { StateCreator } from 'zustand';
import { createLens } from '@dhmk/zustand-lens';

/**
 * Slice interface for UI state.
 */
export interface UISlice {
  /**
   * Indicates the layout direction of the shown panels.
   */
  layoutDirection: LayoutDirection;

  /**
   * Sets the layout direction.
   */
  setLayoutDirection: (layoutDirection: LayoutDirection) => void;

  /** Dynamic header title shown in the global header. */
  headerTitle?: string;

  /** Optional handler for back action in the global header. */
  headerBack?: (() => void) | null;

  /** Optional right-side React node to render before the menu. */
  headerRightAction?: React.ReactNode | null;

  /** Sets the dynamic header state. */
  setHeader: (state: {
    title?: string;
    back?: (() => void) | null;
    rightAction?: React.ReactNode | null;
  }) => void;

  /** Clears dynamic header state. */
  clearHeader: () => void;

  /**
   * Active tab of the context configure view (environment variables vs.
   * iterators). Persisted here so it survives the view being unmounted, e.g.
   * when navigating away to edit an iterator and back.
   */
  configureViewTab: string;

  /** Sets the active tab of the context configure view. */
  setConfigureViewTab: (tab: string) => void;
}

/**
 * Slice for UI state.
 */
export const createUISlice: StateCreator<
  { ui: UISlice },
  [['zustand/immer', never]],
  [],
  UISlice
> = (_set, _get) => {
  // Slices make use of a namespace, make current slice local namespace.
  const [set, get] = createLens(_set, _get, ['ui']);

  return {
    layoutDirection: LayoutDirection.Row,

    setLayoutDirection: (layoutDirection) => {
      set(() => ({ layoutDirection }));
    },

    headerTitle: undefined,
    headerBack: null,
    headerRightAction: null,
    setHeader: ({ title, back, rightAction }) => {
      const prevTitle = get().headerTitle;
      const prevBack = get().headerBack;
      const prevRight = get().headerRightAction;
      const nextTitle = title;
      const nextBack = back ?? null;
      const nextRight = rightAction ?? null;

      const unchanged =
        prevTitle === nextTitle && prevBack === nextBack && prevRight === nextRight;
      if (unchanged) return;

      set(() => ({
        headerTitle: nextTitle,
        headerBack: nextBack,
        headerRightAction: nextRight,
      }));
    },
    clearHeader: () => {
      const prevTitle = get().headerTitle;
      const prevBack = get().headerBack;
      const prevRight = get().headerRightAction;
      if (prevTitle === undefined && prevBack === null && prevRight === null) {
        return;
      }
      set(() => ({ headerTitle: undefined, headerBack: null, headerRightAction: null }));
    },

    configureViewTab: 'env-variables',
    setConfigureViewTab: (tab) => {
      set(() => ({ configureViewTab: tab }));
    },
  };
};
